import {EventBus} from "../../../common/ts/events/EventBus"
import {
    PushManagerSubscriptionCreatedEvent,
    PushManagerSubscriptionStatus
} from "./events";

export class WPNManager {

    private readonly publicVapidKey: string
    private readonly swScriptUrl: string
    private readonly eventBus: EventBus
    private safariBannerShown = false

    constructor(
        publicVapidKey: string,
        swScriptUrl: string,
        eventBus: EventBus
    ) {
        this.publicVapidKey = publicVapidKey
        this.swScriptUrl = swScriptUrl
        this.eventBus = eventBus
    }

    async init() {
        if (this.isActivated()) {
            await this.registerServiceWorker()
            await this.checkPermissions()
        }
    }

    private async checkPermissions() {
        if (this.isFromSafariBrowser()) {
            await this.handleSafariPermissions()
        } else if (this.isBrowserCompatibleWithWebPush()) {
            await this.handlePermissions()
        }
    }

    private async handlePermissions() {
        if (Notification.permission === 'default') {
            Notification.requestPermission().then(permission => {
                if (permission === 'granted') {
                    //this.trackWebPushGranted()
                    this.pushManagerSubscriptionCreated(PushManagerSubscriptionStatus.ALLOWED)
                } else if (permission === 'denied') {
                    //this.trackWebPushDenied()
                } else {
                    //this.trackWebPushDenied()
                }
            })
        } else if (Notification.permission === 'granted') {
            //this.trackWebPushGranted()
            await this.pushManagerSubscriptionCreated(PushManagerSubscriptionStatus.GRANTED)
        } else if (Notification.permission === 'denied') {
            //this.trackWebPushDenied()
        }

    }

    private async handleSafariPermissions() {
        const userAccepted = await this.showSafariPermissionBanner();
        console.log('Safari permission banner result:', userAccepted);
        if (!userAccepted) return; // User denied, do not proceed
        const safariPush = (window as any).safari.pushNotification;
        const permissionData = safariPush.permission('web.com.mitula.homes');
        console.log('Safari permission data:', permissionData);
        if (permissionData.permission === 'default') {
            console.log('Requesting Safari push permission...');
            await safariPush.requestPermission(
                'https://www.mitula.com/', // Push package server URL
                'web.com.mitula.homes',        // Website push ID
                {},                        // Data (can be empty)
                /*(permissionResult: any) => {
                    console.log('Safari permission result:', permissionResult);
                    if (permissionResult.permission === 'granted') {
                        console.log('GRANTED');
                        this.pushManagerSubscriptionCreated(PushManagerSubscriptionStatus.ALLOWED);
                    } else if (permissionResult.permission === 'denied') {
                        console.log('DENIED');
                        // Optionally handle denied
                    }
                }*/
                this.handleSafariPermissions.bind(this)
            );
        } else if (permissionData.permission === 'granted') {
            console.log('GRANTED 2');
            await this.pushManagerSubscriptionCreated(PushManagerSubscriptionStatus.GRANTED);
        } else if (permissionData.permission === 'denied') {
            // Optionally handle denied
            console.log('DENIED 2');
        }
        return
    }

    private async registerServiceWorker() {
        await navigator.serviceWorker.register(this.swScriptUrl)
    }

    private async pushManagerSubscriptionCreated(status: PushManagerSubscriptionStatus) {
        const subscription = await this.getPushSubscription()
        const key = subscription.getKey("p256dh");
        const auth = subscription.getKey("auth");

        if (key && auth) {
            this.eventBus.emit(new PushManagerSubscriptionCreatedEvent(
                {
                    endpoint: subscription.endpoint,
                    auth: this.arrayBufferToBase64(auth),
                    key: this.arrayBufferToBase64(key),
                    status: status,
                }
            ))
        }
    }

    private async getPushSubscription() {
        const serviceWorkerRegistration = await navigator.serviceWorker.ready
        const subscription = await serviceWorkerRegistration.pushManager.getSubscription()
        if (subscription) return subscription
        return await serviceWorkerRegistration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: this.urlBase64ToUint8Array(this.publicVapidKey),
        });
    }

    private isBrowserCompatibleWithWebPush(): boolean {
        const hasServiceWorker = 'serviceWorker' in navigator
        const hasPushManager = 'PushManager' in window
        const hasNotification = 'Notification' in window
        const hasShowNotification =
            typeof ServiceWorkerRegistration !== 'undefined' &&
            'showNotification' in ServiceWorkerRegistration.prototype
        return (
            hasServiceWorker &&
            hasPushManager &&
            hasNotification &&
            hasShowNotification
        )
    }

    private urlBase64ToUint8Array = (base64String: string) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4)
        const base64 = (base64String + padding)
            .replace(/\-/g, '+')
            .replace(/_/g, '/')

        const rawData = window.atob(base64)
        const outputArray = new Uint8Array(rawData.length)

        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i)
        }
        return outputArray
    }

    private arrayBufferToBase64 = (arrayBuffer: ArrayBuffer) => {
        return btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(arrayBuffer))));
    }

    private isActivated(): boolean {
        const params = new URLSearchParams(window.location.search);
        return params.get('PROD-13007') === 'true';
    }

    private isFromSafariBrowser() {
        const userAgent = navigator.userAgent.toLowerCase();
        // @ts-ignore
        return (userAgent.indexOf('safari') !== -1 && 'safari' in window && 'pushNotification' in window.safari);
    }

    private showSafariPermissionBanner(): Promise<boolean> {
        if (this.safariBannerShown) return Promise.resolve(true)
        this.safariBannerShown = true
        return new Promise(resolve => {
            // Create banner element
            const banner = document.createElement('div');
            banner.id = 'safari-permission-banner';
            banner.style.position = 'fixed';
            banner.style.top = '0';
            banner.style.left = '0';
            banner.style.width = '100%';
            banner.style.background = '#eff8ff';
            banner.style.color = '#0073d9';
            banner.style.zIndex = '9999';
            banner.style.display = 'flex';
            banner.style.justifyContent = 'space-between';
            banner.style.alignItems = 'center';
            banner.style.padding = '12px 24px';
            banner.style.boxShadow = '0 2px 8px rgba(0,0,0,0.07)';

            // Message
            const message = document.createElement('span');
            message.textContent = 'This site would like to send you notifications. Please accept or deny.';
            message.style.flex = '1';
            message.style.fontSize = '16px';

            // Buttons
            const btnAccept = document.createElement('button');
            btnAccept.textContent = 'Accept';
            btnAccept.style.marginRight = '12px';
            btnAccept.style.background = '#0073d9';
            btnAccept.style.color = '#fff';
            btnAccept.style.border = 'none';
            btnAccept.style.borderRadius = '3px';
            btnAccept.style.padding = '8px 18px';
            btnAccept.style.fontSize = '15px';
            btnAccept.style.cursor = 'pointer';

            const btnDeny = document.createElement('button');
            btnDeny.textContent = 'Deny';
            btnDeny.style.background = '#fff';
            btnDeny.style.color = '#0073d9';
            btnDeny.style.border = '1px solid #0073d9';
            btnDeny.style.borderRadius = '3px';
            btnDeny.style.padding = '8px 18px';
            btnDeny.style.fontSize = '15px';
            btnDeny.style.cursor = 'pointer';

            // Button actions
            btnAccept.onclick = () => {
                banner.remove();
                resolve(true);
            };
            btnDeny.onclick = () => {
                banner.remove();
                resolve(false);
            };

            // Add elements
            banner.appendChild(message);
            banner.appendChild(btnAccept);
            banner.appendChild(btnDeny);
            document.body.prepend(banner);
        });
    }
}
